// Spring Page structure
export interface Page<T> {
    content: T[];
    pageable: PageableObject;
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
    sort: SortObject;
    numberOfElements: number;
    first: boolean;
    last: boolean;
    empty: boolean;
}

export interface Pageable {
    page?: number;
    size?: number;
    sort?: string[];
}

export interface PageableObject {
    offset: number;
    sort: SortObject;
    paged: boolean;
    pageNumber: number;
    pageSize: number;
    unpaged: boolean;
}

export interface SortObject {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
}

export interface RangeLong {
    start?: number;
    end?: number;
}

// Helper to build sort params
export function buildSortParam(field: string, direction: 'asc' | 'desc'): string {
    return `${field},${direction}`;
}

// Helper to build pageable params
export function buildPageable(page: number, size: number, sort?: string[]): Pageable {
    return {
        page,
        size,
        sort: sort || []
    };
}
/**
 * Forma paginata asa cum o produce generatorul: totul optional, iar campurile declarate
 * `int64` in spec ajung `bigint` in TypeScript.
 */
export interface GeneratedPage<T> {
    content?: T[];
    pageable?: {
        offset?: bigint | number;
        sort?: { empty?: boolean; sorted?: boolean; unsorted?: boolean };
        paged?: boolean;
        pageNumber?: number;
        pageSize?: number;
        unpaged?: boolean;
    };
    totalElements?: bigint | number;
    totalPages?: number;
    size?: number;
    number?: number;
    sort?: { empty?: boolean; sorted?: boolean; unsorted?: boolean };
    numberOfElements?: number;
    first?: boolean;
    last?: boolean;
    empty?: boolean;
}

/**
 * Forma generata -> forma folosita in aplicatie.
 *
 * Cele doua difera pe doua axe, si de aceea existau 9 cast-uri prin `as`:
 *
 *  1. OPTIONALITATE — generatorul marcheaza tot cu `?`, pentru ca spec-ul n-are `required`.
 *     Componentele au nevoie de `content: T[]`, nu de `T[] | undefined`.
 *  2. PRIMITIV — `totalElements` si `pageable.offset` sunt declarate `int64` in spec, deci
 *     ajung `bigint`. La RUNTIME sunt numere obisnuite: transformerele generate ruleaza doar
 *     sub `if (opts.responseType === "json")`, iar `responseType` nu e setat nicaieri pe calea
 *     SDK-ului. `Number()` e corect in ambele situatii — si daca transformerele s-ar activa
 *     vreodata, aici e singurul loc care trebuie sa stie.
 *
 * O functie pura cu contract explicit, in loc de un cast care ascunde ambele diferente.
 */
export function normalizePage<T>(page: GeneratedPage<T>): Page<T> {
    const sort = {
        empty: page.sort?.empty ?? true,
        sorted: page.sort?.sorted ?? false,
        unsorted: page.sort?.unsorted ?? true,
    };
    const content = page.content ?? [];

    return {
        content,
        totalElements: Number(page.totalElements ?? 0),
        totalPages: page.totalPages ?? 0,
        size: page.size ?? content.length,
        number: page.number ?? 0,
        numberOfElements: page.numberOfElements ?? content.length,
        first: page.first ?? true,
        last: page.last ?? true,
        empty: page.empty ?? content.length === 0,
        sort,
        pageable: {
            offset: Number(page.pageable?.offset ?? 0),
            pageNumber: page.pageable?.pageNumber ?? 0,
            pageSize: page.pageable?.pageSize ?? (page.size ?? content.length),
            paged: page.pageable?.paged ?? true,
            unpaged: page.pageable?.unpaged ?? false,
            sort: {
                empty: page.pageable?.sort?.empty ?? sort.empty,
                sorted: page.pageable?.sort?.sorted ?? sort.sorted,
                unsorted: page.pageable?.sort?.unsorted ?? sort.unsorted,
            },
        },
    };
}
